import { AuthState } from './useAuthStateModel';

/**
 * A plain-language, "explain it like I'm 5" walkthrough keyed to the currently
 * detected AuthState. Pure data in/out (no JSX, no React, no side effects) so
 * it is unit-testable and the Dialog that renders it stays a thin view. This
 * replaces the old collapsible "Login instructions" block.
 */
export interface HelpWalkthrough {
  title: string;
  steps: string[];
}

const DEFAULT: HelpWalkthrough = {
  title: 'How to sign in',
  steps: [
    'Type your email in the box.',
    'Press the big button under the box.',
    'Follow the prompt your device shows you (like a fingerprint or face check).',
    'Not sure what to do? The message above the button tells you the next step for your situation.',
  ],
};

/**
 * Returns a step-by-step guide for the given state. `null` (or any unmapped
 * state) returns the generic default guide.
 */
export function authHelpSteps(state: AuthState | null): HelpWalkthrough {
  switch (state) {
    case AuthState.ReadyToSignIn:
      return {
        title: "You're all set",
        steps: [
          'Press the button under your email.',
          "Your device will ask you to prove it's you (fingerprint, face, or PIN).",
          "That's it — you're in.",
        ],
      };

    case AuthState.Unknown:
      return {
        title: "We don't know this email yet",
        steps: [
          "We don't recognize that email on this device.",
          'Press Request access to ask an admin to let you in.',
          'Wait for an admin to approve it.',
          'Come back, type your email again, and press the button.',
        ],
      };

    case AuthState.UserKnownDeviceUnknown:
      return {
        title: 'New device for your account',
        steps: [
          'We know your account, but this device has never been set up.',
          'Easiest path: press Request access, wait for an admin to approve, then come back and press Add this device.',
          'Already have a passkey on your phone? Press "Use my phone" instead — your browser will show a pop-up (look for "use a phone or tablet") so you can sign in with your phone.',
          'The phone option needs Bluetooth on and is run by your browser, so it may look a little different on each device.',
        ],
      };

    case AuthState.DeviceRequestPending:
      return {
        title: 'Waiting on an admin',
        steps: [
          "You've already asked for access from this device.",
          'An admin needs to approve it — that can take a few minutes.',
          'Press Check again to see if it went through.',
          'Once approved, you can finish setting up this device.',
        ],
      };

    case AuthState.ApprovalExpired:
      return {
        title: 'Your approval ran out',
        steps: [
          'An admin approved you, but the short approval window passed before you finished.',
          'Press Request access to ask again.',
          'When the admin approves, come straight back and finish — the window is short.',
        ],
      };

    case AuthState.DeviceNotApproved:
      return {
        title: 'That request is closed',
        steps: [
          'The earlier request for this device is no longer open.',
          'Press Request access to start a fresh one.',
          'Wait for an admin to approve, then come back and finish.',
        ],
      };

    case AuthState.NewUserNeedsInvite:
      return {
        title: 'You need an invitation',
        steps: [
          'Setting up a new account needs an invite from an admin.',
          'If your invite is still pending, press Check again after an admin approves it.',
          'If it expired, press Request access and ask an admin to send a new one.',
        ],
      };

    case AuthState.PasskeyDismissed:
      return {
        title: 'The prompt closed',
        steps: [
          'The sign-in pop-up was closed or timed out before it finished.',
          'Press Try again.',
          'This time, complete the fingerprint, face, or PIN check your device shows.',
        ],
      };

    case AuthState.PasskeyDismissedCanAddDevice:
      return {
        title: 'The prompt closed',
        steps: [
          'The sign-in pop-up closed before it finished.',
          'Press Try again and complete the device check.',
          'If this browser has never had a passkey, press Add this device instead to set one up.',
        ],
      };

    case AuthState.PasskeyNoMatch:
      return {
        title: "That passkey didn't work",
        steps: [
          "The passkey on this device didn't match.",
          'Press Add this device to set up a fresh passkey here.',
          'Follow your device prompt to create it.',
        ],
      };

    default:
      return DEFAULT;
  }
}
